import React, { useState, useRef, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";

const CORRECT_PIN = "6789";

export default function PinDialog({ open, onOpenChange, onConfirm, title, description }) {
  const [pin, setPin] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    if (open) {
      setPin(["", "", "", ""]);
      setError(false);
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
  }, [open]);

  const handleChange = (idx, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...pin];
    next[idx] = value;
    setPin(next);
    setError(false);
    if (value && idx < 3) {
      inputRefs[idx + 1].current?.focus();
    }
    if (idx === 3 && value) {
      const code = next.join("");
      if (code === CORRECT_PIN) {
        onOpenChange(false);
        onConfirm();
      } else {
        setError(true);
        setPin(["", "", "", ""]);
        setTimeout(() => inputRefs[0].current?.focus(), 150);
      }
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !pin[idx] && idx > 0) {
      inputRefs[idx - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted.length === 4) {
      const next = pasted.split("");
      setPin(next);
      if (pasted === CORRECT_PIN) {
        onOpenChange(false);
        onConfirm();
      } else {
        setError(true);
        setPin(["", "", "", ""]);
        setTimeout(() => inputRefs[0].current?.focus(), 150);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-xs" data-testid="pin-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2 text-base">
            <ShieldAlert className="w-5 h-5 text-red-400" />
            {title || "Xác nhận xóa"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-400">{description || "Nhập mã PIN để xác nhận thao tác này."}</p>
          <div className="flex justify-center gap-3" onPaste={handlePaste}>
            {pin.map((d, i) => (
              <input
                key={i}
                ref={inputRefs[i]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className={`w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 bg-[#0f172a] text-white outline-none transition-colors ${error ? "border-red-500 animate-shake" : d ? "border-lime-500" : "border-[#334155] focus:border-sky-500"}`}
                data-testid={`pin-input-${i}`}
              />
            ))}
          </div>
          {error && <p className="text-center text-xs text-red-400" data-testid="pin-error">Sai mã PIN. Vui lòng thử lại.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Hủy</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
